import React from "react";
import { useEffect, useState, useCallback } from "react";
import { useLazyQuery } from "@apollo/client";
import { GET_CLIENTS_QUERY } from "../../graphql/queries/clients";
import Loader from "../../components/loader/loader";
import Table from "../../components/table/table";
import TableCellRenderer from "../../components/table/table-cell-renderer";
import Button from "../../components/Button/Button";
import Modal from "../../components/modal/Modal";
import AddClient from "../../components/add-client/add-client";
import DebouncedInput from "../../components/debounced-input/debounced-input";
import { useNavigate } from "react-router-dom";
import { SKIP, FIRST } from "../../utils/pagination";
import stringifyObject from "../../utils/objectToString";
import ClientsFilters from "../../components/clients-filters/clients-filters";
import stringifyArray from "../../utils/arrayToString";
import hasValue from "../../utils/hasValue";
import useUserRoleStore from "../../store/user-role";
import { isAdmin } from "../../utils/roles";

const ClientPage = () => {
  const [addClientModal, setAddClientModal] = useState(false);
  const [refetchData, setRefetchData] = useState(false);
  const [searchValue, setSearchValue] = useState(undefined);
  const [sortField, setSortField] = useState("name"); // default sort field
  const [sortOrder, setSortOrder] = useState("asc"); // default sort order
  const [filtersModal, setFiltersModal] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState({});
  const [paginationInfo, setPaginationInfo] = useState({
    hasNextPage: undefined,
    hasPreviousPage: undefined,
    totalPages: undefined,
    totalRecords: undefined,
    currentPage: 1,
  });
  const navigate = useNavigate();

  const getPackagesValues = (packages) => {
    if (!packages?.length) {
      return undefined;
    }
    return packages.map((item) => parseInt(item.value, 10));
  };

  const getTowerValues = (towers) => {
    if (!towers?.length > 0) {
      return undefined;
    }
    return towers.map((item) => item.value);
  };

  const [fetchClients, { data, loading, error }] = useLazyQuery(
    GET_CLIENTS_QUERY,
    {
      variables: {
        search: searchValue,
        first: FIRST,
        skip: (paginationInfo.currentPage - 1) * SKIP,
        sortField: sortField,
        sortOrder: sortOrder,
        towers: getTowerValues(appliedFilters.towers),
        packages: getPackagesValues(appliedFilters.packages),
      },
      fetchPolicy: "network-only",
    }
  );

  const onEditData = () => {};

  const onSendMessage = () => {};

  const canEditData = () => {
    return true;
  };

  const canSendMessage = () => {
    return true;
  };

  const { role } = useUserRoleStore();

  // Editing a client is an admin action, so staff get no Actions column at all.
  const tableActions = !isAdmin(role)
    ? []
    : [
        {
          name: "Edit",
          onClick: onEditData,
          shouldShow: canEditData,
        },
        {
          name: "Send Message",
          onClick: onSendMessage,
          shouldShow: canSendMessage,
        },
      ];

  useEffect(() => {
    if (data) {
      setPaginationInfo((prev) => ({
        ...prev,
        currentPage: data.clients.currentPage,
        hasNextPage: data.clients.hasNextPage,
        hasPreviousPage: data.clients.hasPreviousPage,
        totalPages: data.clients.totalPages,
        totalRecords: data.clients.totalRecords,
      }));
    }
  }, [data]);

  const onNextPageClick = () => {
    setPaginationInfo((prev) => ({
      ...prev,
      currentPage: prev?.currentPage + 1,
    }));
    setRefetchData(true); // Trigger refetch
  };

  const onPrevPageClick = () => {
    setPaginationInfo((prev) => ({
      ...prev,
      currentPage: prev?.currentPage - 1,
    }));
    setRefetchData(true); // Trigger refetch
  };

  const onApplyFilters = () => {
    setFiltersModal(false);
    setRefetchData(true);
  };

  useEffect(() => {
    if (refetchData) {
      fetchClients();
      setRefetchData(false); // Reset the refetch flag
    }
  }, [refetchData]);

  const onAddClient = () => {
    setAddClientModal((prev) => !prev);
  };

  const onAddClientClose = () => {
    setRefetchData(true);
    setAddClientModal(false);
  };

  const handleInputChange = useCallback((e) => {
    setSearchValue(e.target.value);
    setPaginationInfo((prev) => ({
      ...prev,
      currentPage: 1, // Reset pagination when search changes
    }));
    setRefetchData(true); // Trigger refetch
  }, []);

  const onRowClick = (data) => {
    navigate(`/client-details/${data.id}`);
  };

  const handleSortChange = (field) => {
    const isAsc = sortField === field && sortOrder === "asc";
    setSortOrder(isAsc ? "desc" : "asc");
    setSortField(field);
    setPaginationInfo((prev) => ({
      ...prev,
      currentPage: 1, // Reset pagination when sort changes
    }));
    setRefetchData(true); // Trigger refetch
  };

  useEffect(() => {
    fetchClients();
  }, [searchValue, sortField, sortOrder]);

  const onClearFilters = () => {
    setSearchValue("");
    setAppliedFilters([]);
    setRefetchData(true);
  };

  const onFilter = () => {
    setFiltersModal((prev) => !prev);
  };

  if (error) {
    return <div className="display-page__error">Something went wrong</div>;
  }

  return (
    <div className="display-page">
      <Modal
        modal={addClientModal}
        onClose={onAddClientClose}
        modalSize="large"
      >
        <>
          <AddClient onCancel={onAddClientClose} />
        </>
      </Modal>
      <Modal modal={filtersModal} onClose={onFilter} modalSize="large">
        <>
          <ClientsFilters
            onFilter={onFilter}
            appliedFilters={appliedFilters}
            setAppliedFilters={setAppliedFilters}
            onApplyFilters={onApplyFilters}
          />
        </>
      </Modal>
      <div className="display-page__header">
        <div className="display-page__search-input">
          <img
            src="/images/search.svg"
            alt="search-icon"
            className="display-page__search-icon"
          />
          <DebouncedInput
            className="input-group input-group__search"
            debounceTimeout={400}
            minLength={3}
            onChange={(e) => {
              handleInputChange(e);
            }}
            value={searchValue}
          />
        </div>
        <div className="display-page__header-column display-page__filter">
          <Button
            onClick={onFilter}
            name={"Filter"}
            className={"button--primary"}
          />
        </div>
        {isAdmin(role) && (
          <Button
            onClick={onAddClient}
            name={"+ Add Client"}
            className={"button--primary"}
          />
        )}
      </div>
      <div className="display-page__sub-header">
        {hasValue(appliedFilters) && (
          <div className="display-page__filters">
            <div className="display-page__sub-header-text">
              Applied Filters:
              <div className="display-page__sub-header-text">
                {stringifyArray(appliedFilters.packages)},{" "}
                {stringifyArray(appliedFilters.towers)}
              </div>
            </div>
            <div
              onClick={onClearFilters}
              className="display-page__filters-clear"
            >
              Clear Filters
            </div>
          </div>
        )}
      </div>
      <div className="display-page__sub-header">
        {searchValue && (
          <div className="display-page__filters">
            <div className="display-page__sub-header-text">
              Applied Filters:
              <div className="display-page__sub-header-text">
                {stringifyObject({
                  search: searchValue !== "" ? searchValue : undefined,
                })}
              </div>
            </div>
            <div
              onClick={onClearFilters}
              className="display-page__filters-clear"
            >
              Clear Filters
            </div>
          </div>
        )}
      </div>
      {!loading && (
        <div className="display-page__info-wrapper">
          Displaying{" "}
          <span className="display-page__info--bold">
            {paginationInfo.currentPage === 1
              ? 1
              : (paginationInfo.currentPage - 1) * SKIP + 1}{" "}
          </span>
          to{" "}
          <span className="display-page__info--bold">
            {paginationInfo.currentPage && (
              <>
                {" "}
                {Math.min(
                  paginationInfo.currentPage * SKIP,
                  paginationInfo.totalRecords
                )}
              </>
            )}
          </span>{" "}
          of{" "}
          <span className="display-page__info--bold">
            {paginationInfo.totalRecords}
          </span>{" "}
          total.
        </div>
      )}
      <div className="details-page__table">
        {loading ? (
          <Loader sizeClass={"size--medium"} />
        ) : (
          <Table
            data={data?.clients?.items}
            onRowClick={onRowClick}
            columns={[
              {
                field: "clientId",
                title: "Client ID",
                showOnMobile: true,
                hideMobileUp: false,
                render: ({ clientId }) => {
                  return <TableCellRenderer firstItem={clientId} />;
                },
              },
              {
                field: "name",
                title: "Name",
                showOnMobile: true,
                hideMobileUp: false,
                sortable: true,
                render: ({ name, surname }) => {
                  return <TableCellRenderer firstItem={`${name} ${surname}`} />;
                },
              },
              {
                field: "channel",
                title: "Channel",
                showOnMobile: true,
                hideMobileUp: false,
                sortable: true,
                render: ({ channel, tower }) => {
                  return (
                    <TableCellRenderer
                      firstItem={`${tower.zonkeId} - ${channel}`}
                    />
                  );
                },
              },
              {
                field: "package",
                title: "Package",
                showOnMobile: true,
                hideMobileUp: false,
                render: ({ package: { name } }) => {
                  return <TableCellRenderer firstItem={name} />;
                },
              },
            ]}
            actions={tableActions}
            paged
            hasNextPage={paginationInfo.hasNextPage}
            hasPrevPage={paginationInfo.hasPreviousPage}
            onNextPage={onNextPageClick}
            onPrevPage={onPrevPageClick}
            sortConfig={{ key: sortField, direction: sortOrder }}
            onSortChange={handleSortChange}
          />
        )}
      </div>
    </div>
  );
};

export default ClientPage;
